import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { scrollToId } from "../lib/scroll";
import { EASE } from "./Reveal";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 240]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      id="home"
      data-testid="hero-section"
      className="relative z-10 flex min-h-screen items-center justify-center overflow-hidden"
    >
      <motion.div style={{ y, opacity }} className="relative z-10 px-6 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.98, filter: "blur(12px)" }}
          animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
          transition={{ delay: 1.2, duration: 2.6, ease: "easeOut" }}
          className="relative flex justify-center"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.55 }}
            transition={{ delay: 1.8, duration: 3.2, ease: "easeOut" }}
            className="absolute left-1/2 top-1/2 -z-10 h-[70%] w-[110%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(ellipse,rgba(42,82,190,0.35),transparent_70%)] blur-3xl"
          />
          <img
            src="/assets/cosmic-dreams-logo-hero.png"
            alt="COSMIC DREAMS — official logo"
            data-testid="hero-logo"
            className="w-64 sm:w-80 md:w-[26rem] lg:w-[28rem]"
          />
          <div aria-hidden="true" className="pointer-events-none absolute inset-0">
            {[
              { x: "6%", y: "40%", delay: 4.2, color: "rgba(140,190,255,0.85)", size: "lg" },
              { x: "94%", y: "38%", delay: 5.4, color: "rgba(190,160,255,0.85)", size: "lg" },
              { x: "50%", y: "6%", delay: 6.6, color: "rgba(120,200,255,0.9)", size: "sm" },
              { x: "88%", y: "94%", delay: 7.8, color: "rgba(230,240,255,0.9)", size: "sm" },
            ].map((f, i) => (
              <motion.div
                key={i}
                data-testid={`logo-flare-${i}`}
                className="absolute h-0 w-0"
                style={{ left: f.x, top: f.y }}
                initial={{ opacity: 0, scale: 0.4 }}
                animate={{ opacity: [0, 1, 0], scale: [0.4, 1, 0.85] }}
                transition={{ delay: f.delay, duration: 2, times: [0, 0.35, 1], ease: "easeOut" }}
              >
                <div
                  className={`absolute left-0 top-0 -translate-x-1/2 -translate-y-1/2 rounded-full blur-md ${f.size === "lg" ? "h-16 w-16" : "h-10 w-10"}`}
                  style={{ background: `radial-gradient(circle, rgba(255,255,255,0.95), ${f.color} 45%, transparent 72%)` }}
                />
                <div
                  className={`absolute left-0 top-0 -translate-x-1/2 -translate-y-1/2 blur-[1px] ${f.size === "lg" ? "h-[2px] w-40" : "h-[1.5px] w-24"}`}
                  style={{ background: `linear-gradient(90deg, transparent, ${f.color}, transparent)` }}
                />
                <div
                  className={`absolute left-0 top-0 -translate-x-1/2 -translate-y-1/2 blur-[1px] ${f.size === "lg" ? "h-28 w-[2px]" : "h-16 w-[1.5px]"}`}
                  style={{ background: `linear-gradient(180deg, transparent, ${f.color}, transparent)` }}
                />
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 3.2, duration: 1.6, ease: EASE }}
          className="mx-auto mt-6 md:mt-8 h-px w-40 md:w-64 bg-gradient-to-r from-transparent via-[#D4AF37]/70 to-transparent"
        />

        <motion.p
          initial={{ opacity: 0, letterSpacing: "0.9em" }}
          animate={{ opacity: 1, letterSpacing: "0.5em" }}
          transition={{ delay: 3.5, duration: 1.8, ease: EASE }}
          data-testid="hero-tagline"
          className="mt-5 md:mt-6 font-serif text-xs md:text-sm text-[#D4AF37] uppercase tracking-[0.35em]"
        >
          Dream beyond reality.
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 3.9, duration: 1.4, ease: EASE }}
          className="mx-auto mt-4 md:mt-5 max-w-2xl px-2 font-serif text-base md:text-xl tracking-wide text-[#F5F5FA]/90"
        >
          A Connected Universe of Heroes, Myths, Worlds and Stories.
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 4.3, duration: 1.6 }}
          data-testid="hero-creator-credit"
          className="mt-4 md:mt-5 font-mono text-[10px] md:text-xs tracking-[0.3em] text-[#A0A0B0] uppercase"
        >
          From the mind of Pandrangi Aditya Kiran
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 4.7, duration: 1.4, ease: EASE }}
          className="mt-7 md:mt-9 flex flex-col items-center justify-center gap-5 sm:flex-row"
        >
          <button
            data-testid="enter-universe-btn"
            onClick={() => scrollToId("universe")}
            className="group relative overflow-hidden border border-white/20 px-10 py-4 font-mono text-[11px] tracking-[0.35em] text-white transition-colors duration-700 hover:border-[#D4AF37]/70 hover:text-[#D4AF37]"
          >
            <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-[#D4AF37]/10 to-transparent transition-transform duration-1000 group-hover:translate-x-full" />
            EXPLORE THE UNIVERSE
          </button>
          <button
            data-testid="enter-story-btn"
            onClick={() => scrollToId("books")}
            className="font-mono text-[11px] tracking-[0.35em] text-[#A0A0B0] underline decoration-white/20 underline-offset-8 transition-colors duration-500 hover:text-[#D4AF37] hover:decoration-[#D4AF37]/60"
          >
            ENTER THE STORY
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 5.2, duration: 1.6 }}
        className="absolute bottom-10 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-3"
      >
        <span className="font-mono text-[9px] tracking-[0.4em] text-[#606070] uppercase">Scroll</span>
        <span className="scroll-cue-line block h-14 w-px bg-gradient-to-b from-[#D4AF37]/60 to-transparent" />
      </motion.div>

      <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-b from-transparent to-[#030305]" />
    </section>
  );
};

export default Hero;
